import * as R from 'ramda';
import { put, call, fork, select, takeLatest } from 'redux-saga/effects';
// components
import { closeModal } from '../../../../components/modal/actions';
import { openLoader, closeLoader } from '../../../../components/loader/actions';
// features
import { makeSelectCurrentBranchGuid } from '../../../branch/selectors';
// helpers/constants
import * as G from '../../../../helpers';
import * as GC from '../../../../constants';
// sagas
import { crudSaga, visitPageSaga } from '../../../../sagas';
// utilities
import { sendRequest } from '../../../../utilities/http';
import endpointsMap from '../../../../utilities/endpoints';
// feature configs
import * as A from '../../actions';
import {
  getConfigByGroupSaga,
  getReferenceTypesByScopeForConfigSaga,
} from '../../sagas';
//////////////////////////////////////////////////

function* getConfigsForUiConfigSaga() {
  try {
    const branchGuid = yield select(makeSelectCurrentBranchGuid());

    const options = {
      params: {
        names: GC.TRUCK_TRUCK_TYPE,
        [GC.FIELD_BRANCH_GUID]: branchGuid,
      },
    };

    yield call(crudSaga, {
      options,
      method: 'get',
      mapper: G.mapConfigValuesByName,
      parentSagaName: 'getConfigsForUiConfigSaga',
      endpoint: endpointsMap.branchConfigsEndpoint,
      successAction: A.getConfigsForUiConfigSuccess,
    });
  } catch (error) {
    yield call(G.handleException, error, 'getConfigsForUiConfigSaga exception');
  }
}

function* getCloDispatchBoardActionListSaga({ payload }) {
  try {
    const options = {
      params: {
        [GC.FIELD_BRANCH_GUID]: payload,
      },
    };

    const res = yield call(sendRequest, 'get', endpointsMap.getCloDispatchBoardActionList, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.getCloDispatchBoardActionListSuccess(data));
    } else {
      yield call(G.handleFailResponse, res, 'getCloDispatchBoardActionListSaga fail');
    }
  } catch (error) {
    yield call(G.handleException, error, 'getCloDispatchBoardActionListSaga exception');
  }
}

function* getTelDispatchBoardActionListSaga({ payload }) {
  try {
    const options = {
      params: {
        [GC.FIELD_BRANCH_GUID]: payload,
      },
    };

    const res = yield call(sendRequest, 'get', endpointsMap.getTelDispatchBoardActionList, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.getTelDispatchBoardActionListSuccess(data));
    } else {
      yield call(G.handleFailResponse, res, 'getTelDispatchBoardActionListSaga fail');
    }
  } catch (error) {
    yield call(G.handleException, error, 'getTelDispatchBoardActionListSaga exception');
  }
}

function* getCentralizedDispatchActionListSaga({ payload }) {
  try {
    const options = {
      params: {
        [GC.FIELD_BRANCH_GUID]: payload,
      },
    };

    const res = yield call(sendRequest, 'get', endpointsMap.getCentralizedDispatchActionList, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.getCentralizedDispatchActionListSuccess(data));
    } else {
      yield call(G.handleFailResponse, res, 'getCentralizedDispatchActionListSaga fail');
    }
  } catch (error) {
    yield call(G.handleException, error, 'getCentralizedDispatchActionListSaga exception');
  }
}

const isCentralizedDispatchActions = (configName) => R.equals(
  configName,
  GC.FIELD_CONFIGURATION_UI_CENTRALIZED_DISPATCH_ACTIONS,
);

const getDispatchActionPanelEndpoint = (loadType, configName) => {
  if (isCentralizedDispatchActions(configName)) return endpointsMap.centralizedDispatchActionPanel;

  return G.ifElse(
    R.equals(loadType, GC.LOAD_TYPE_TEL),
    endpointsMap.telDispatchBoardActionPanel,
    endpointsMap.cloDispatchBoardActionPanel,
  );
};

const getDeleteDispatchActionPanelEndpoint = (guid, loadType, configName) => {
  if (isCentralizedDispatchActions(configName)) {
    return endpointsMap.getCentralizedDispatchActionEndpoint(guid);
  }

  return G.ifElse(
    R.equals(loadType, GC.LOAD_TYPE_TEL),
    endpointsMap.getTelDispatchBoardActionEndpoint,
    endpointsMap.getCloDispatchBoardActionEndpoint,
  )(guid);
};

const getDispatchActionPanelListRequest = (branchGuid, loadType, configName) => {
  if (isCentralizedDispatchActions(configName)) {
    return A.getCentralizedDispatchActionListRequest(branchGuid);
  }

  return G.ifElse(
    R.equals(loadType, GC.LOAD_TYPE_TEL),
    A.getTelDispatchBoardActionListRequest(branchGuid),
    A.getCloDispatchBoardActionListRequest(branchGuid),
  );
};

function* handleSaveDispatchBoardStatusActionSaga({ payload }) {
  try {
    yield put(openLoader({ showDimmer: true }));

    const { data, loadType, configName } = payload;

    const endpoint = getDispatchActionPanelEndpoint(loadType, configName);

    const branchGuid = yield select(makeSelectCurrentBranchGuid());

    const options = {
      data: R.assoc(GC.BRANCH_GUID, branchGuid, data),
    };

    const res = yield call(sendRequest, 'post', endpoint, options);

    const { status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(closeModal());

      yield put(getDispatchActionPanelListRequest(branchGuid, loadType, configName));
    } else {
      yield call(G.handleFailResponse, res, 'handleSaveDispatchBoardStatusActionSaga fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.handleException, error, 'handleSaveDispatchBoardStatusActionSaga exception');
  }
}

function* handleUpdateDispatchBoardStatusActionSaga({ payload }) {
  try {
    yield put(openLoader({ showDimmer: true }));

    const { data, loadType, configName } = payload;

    const endpoint = getDispatchActionPanelEndpoint(loadType, configName);

    const res = yield call(sendRequest, 'put', endpoint, { data });

    const { status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(closeModal());

      const branchGuid = yield select(makeSelectCurrentBranchGuid());

      yield put(getDispatchActionPanelListRequest(branchGuid, loadType, configName));
    } else {
      yield call(G.handleFailResponse, res, 'handleUpdateDispatchBoardStatusActionSaga fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.handleException, error, 'handleUpdateDispatchBoardStatusActionSaga exception');
  }
}

function* handleDeleteDispatchBoardStatusActionSaga({ payload }) {
  try {
    yield put(openLoader({ showDimmer: true }));

    const { guid, loadType, configName } = payload;

    const endpoint = getDeleteDispatchActionPanelEndpoint(guid, loadType, configName);

    const res = yield call(sendRequest, 'delete', endpoint);

    const { status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(closeModal());

      const branchGuid = yield select(makeSelectCurrentBranchGuid());

      yield put(getDispatchActionPanelListRequest(branchGuid, loadType, configName));
    } else {
      yield call(G.handleFailResponse, res, 'handleDeleteDispatchBoardStatusActionSaga fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.handleException, error, 'handleDeleteDispatchBoardStatusActionSaga exception');
  }
}

function* getGeofencingZoneColorConfigSaga({ payload }) {
  try {
    const options = {
      params: {
        [GC.FIELD_BRANCH_GUID]: payload,
      },
    };

    const res = yield call(sendRequest, 'get', endpointsMap.geofencingZoneColorConfig, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.getGeofencingZoneColorConfigSuccess(data));
    } else {
      yield call(G.handleFailResponse, res, 'getGeofencingZoneColorConfigSaga fail');
    }
  } catch (error) {
    yield call(G.handleException, error, 'getGeofencingZoneColorConfigSaga exception');
  }
}

function* handleCreateOrUpdateGeofencingZoneColorConfigSaga({ payload }) {
  try {
    yield put(openLoader());

    const { data, isCreate } = payload;

    const branchGuid = yield select(makeSelectCurrentBranchGuid());

    const options = {
      data: R.assoc(GC.BRANCH_GUID, branchGuid, data),
    };

    const method = G.ifElse(isCreate, 'post', 'put');

    const res = yield call(sendRequest, method, endpointsMap.geofencingZoneColorConfig, options);

    const { status, data: resData } = res;

    if (G.isResponseSuccess(status)) {
      yield put(closeModal());

      yield put(A.getGeofencingZoneColorConfigSuccess(resData));
    } else {
      yield call(G.handleFailResponse, res, 'handleCreateOrUpdateGeofencingZoneColorConfigSaga fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.handleException, error, 'handleCreateOrUpdateGeofencingZoneColorConfigSaga exception');
  }
}

function* handleDeleteGeofencingZoneColorConfigSaga({ payload }) {
  try {
    const options = {
      params: {
        [GC.FIELD_GUID]: payload,
      },
    };

    const res = yield call(sendRequest, 'delete', endpointsMap.geofencingZoneColorConfig, options);

    const { status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.getGeofencingZoneColorConfigSuccess({}));
    } else {
      yield call(G.handleFailResponse, res, 'handleDeleteGeofencingZoneColorConfigSaga fail');
    }
  } catch (error) {
    yield call(G.handleException, error, 'handleDeleteGeofencingZoneColorConfigSaga exception');
  }
}

function* handleVisitConfigUiPageSaga({ payload }) {
  while (true) { // eslint-disable-line
    yield call(visitPageSaga, payload, GC.CHECK_VISIT_CONFIG_UI_PAGE);

    yield put(openLoader({ showDimmer: true }));

    yield call(getReferenceTypesByScopeForConfigSaga, { payload: GC.REF_SCOPE_NAME_CLO });
    yield call(getReferenceTypesByScopeForConfigSaga, { payload: GC.REF_SCOPE_NAME_TEL });
    yield call(getConfigByGroupSaga, GC.UI_CONFIG_GROUP);

    yield fork(getConfigsForUiConfigSaga);

    const branchGuid = yield select(makeSelectCurrentBranchGuid());

    yield put(A.getGeofencingZoneColorConfigRequest(branchGuid));
    yield put(A.getCloDispatchBoardActionListRequest(branchGuid));
    yield put(A.getTelDispatchBoardActionListRequest(branchGuid));
    yield put(A.getCentralizedDispatchActionListRequest(branchGuid));
    yield put(closeLoader());

    break;
  }
}

function* uiConfigWatcherSaga() {
  yield takeLatest(GC.VISIT_CONFIG_UI_PAGE, handleVisitConfigUiPageSaga);
  // dispatch board action panel config
  yield takeLatest(A.getCloDispatchBoardActionListRequest, getCloDispatchBoardActionListSaga);
  yield takeLatest(A.getTelDispatchBoardActionListRequest, getTelDispatchBoardActionListSaga);
  yield takeLatest(A.getCentralizedDispatchActionListRequest, getCentralizedDispatchActionListSaga);
  yield takeLatest(A.saveDispatchBoardStatusActionRequest, handleSaveDispatchBoardStatusActionSaga);
  yield takeLatest(A.updateDispatchBoardStatusActionRequest, handleUpdateDispatchBoardStatusActionSaga);
  yield takeLatest(A.deleteDispatchBoardStatusActionRequest, handleDeleteDispatchBoardStatusActionSaga);
  // geofencing zone color config
  yield takeLatest(A.getGeofencingZoneColorConfigRequest, getGeofencingZoneColorConfigSaga);
  yield takeLatest(A.deleteGeofencingZoneColorConfigRequest, handleDeleteGeofencingZoneColorConfigSaga);
  yield takeLatest(A.createOrUpdateGeofencingZoneColorConfigRequest, handleCreateOrUpdateGeofencingZoneColorConfigSaga);
}

export default uiConfigWatcherSaga;
